/**
 * RaceEntry — a single race on a runner's resume.
 *
 * Pure domain model, no Firebase dependencies.
 */
export class RaceEntry {
  constructor({
    id = null,
    raceName,
    distance,
    time = null,
    type = 'road',
    elevation = null,
    date = null,
    location = null,
    isHighlighted = false,
    isPR = false,
    isPodium = false,
    resultsLink = null,
    notes = null,
    lat = null,
    lng = null,
    country = null,
    isTimed = true,
  } = {}) {
    this.id = id;
    this.raceName = raceName;
    this.distance = distance;
    this.time = time;
    this.type = type;
    this.elevation = elevation;
    this.date = date;
    this.location = location;
    this.isHighlighted = isHighlighted;
    this.isPR = isPR;
    this.isPodium = isPodium;
    this.resultsLink = resultsLink;
    this.notes = notes;
    this.lat = lat;
    this.lng = lng;
    this.country = country;
    this.isTimed = isTimed;
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  static validate(data) {
    const errors = [];

    if (!data.raceName || typeof data.raceName !== 'string' || data.raceName.trim() === '') {
      errors.push('raceName is required');
    } else if (data.raceName.length > 150) {
      errors.push('raceName must be 150 characters or less');
    }

    if (data.distance == null || typeof data.distance !== 'number' || data.distance <= 0) {
      errors.push('distance must be a positive number (km)');
    } else if (data.distance > 10000) {
      errors.push('distance seems unreasonably large');
    }

    if (data.location && typeof data.location === 'string' && data.location.length > 100) {
      errors.push('location must be 100 characters or less');
    }

    if (data.date && typeof data.date === 'string') {
      if (!/^\d{4}(-\d{2}-\d{2})?$/.test(data.date)) {
        errors.push('date must be YYYY or YYYY-MM-DD format');
      }
    }

    if (data.type && !['road', 'trail'].includes(data.type)) {
      errors.push('type must be "road" or "trail"');
    }

    if (data.type === 'trail' && (data.elevation == null || typeof data.elevation !== 'number' || data.elevation <= 0)) {
      errors.push('elevation is required for trail races and must be a positive number');
    }

    if (data.time != null) {
      const isDNF = typeof data.time === 'string' && data.time.toUpperCase() === 'DNF';
      if (!isDNF) {
        if (typeof data.time !== 'string' || !/^\d{1,2}:\d{2}:\d{2}$/.test(data.time)) {
          errors.push('time must be in "HH:MM:SS" format, "DNF", or null');
        }
      }
    }

    return { valid: errors.length === 0, errors };
  }

  // ---------------------------------------------------------------------------
  // Computed getters
  // ---------------------------------------------------------------------------

  /** Parse "HH:MM:SS" → total seconds. Returns null when time is absent or DNF. */
  get timeInSeconds() {
    if (!this.time) return null;
    if (this.time.toUpperCase() === 'DNF') return null;
    const parts = this.time.split(':').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  }

  /** Pace in seconds per km. Null if time or distance unavailable. */
  get pacePerKm() {
    const seconds = this.timeInSeconds;
    if (seconds == null || !this.distance || this.distance <= 0) return null;
    return seconds / this.distance;
  }

  /** Human-readable pace, e.g. 4'12". Null if pace can't be calculated. */
  get formattedPace() {
    const pace = this.pacePerKm;
    if (pace == null) return null;
    const mins = Math.floor(pace / 60);
    const secs = Math.round(pace % 60);
    return `${mins}'${String(secs).padStart(2, '0')}"`;
  }

  /** True when this race is a DNF (Did Not Finish). */
  get isDNF() {
    return typeof this.time === 'string' && this.time.toUpperCase() === 'DNF';
  }

  /**
   * Parse date into a JS Date object.
   * - "YYYY-MM-DD" → Date at that day
   * - "YYYY" → Date at July 1 of that year (midpoint convention)
   * - null/invalid → null
   */
  get parsedDate() {
    if (!this.date) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(this.date)) return new Date(this.date);
    if (/^\d{4}$/.test(this.date)) return new Date(`${this.date}-07-01`);
    return null;
  }

  /** Extract the year from date. Returns null if no date. */
  get year() {
    if (!this.date) return null;
    return parseInt(this.date.substring(0, 4), 10);
  }

  // ---------------------------------------------------------------------------
  // Firestore serialization
  // ---------------------------------------------------------------------------

  toFirestore() {
    return {
      id: this.id,
      raceName: this.raceName,
      distance: this.distance,
      time: this.time,
      type: this.type,
      elevation: this.elevation,
      date: this.date,
      location: this.location,
      isHighlighted: this.isHighlighted,
      isPR: this.isPR,
      isPodium: this.isPodium,
      resultsLink: this.resultsLink,
      notes: this.notes,
      lat: this.lat,
      lng: this.lng,
      country: this.country,
      isTimed: this.isTimed,
    };
  }

  static fromFirestore(data) {
    return new RaceEntry({
      id: data.id ?? null,
      raceName: data.raceName,
      distance: data.distance,
      time: data.time ?? null,
      type: data.type ?? 'road',
      elevation: data.elevation ?? null,
      date: data.date ?? null,
      location: data.location ?? null,
      isHighlighted: data.isHighlighted ?? false,
      isPR: data.isPR ?? false,
      isPodium: data.isPodium ?? false,
      resultsLink: data.resultsLink ?? null,
      notes: data.notes ?? null,
      lat: data.lat ?? null,
      lng: data.lng ?? null,
      country: data.country ?? null,
      isTimed: data.isTimed ?? true,
    });
  }
}
